import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useAppTranslation } from '../../services/i18n';
import { pathOf, targetOptionsFor, WorkplaceNode, WorkplaceTree } from '../../helpers/workplaceTree';
import { pickerInputProps, pickerSlotProps } from '../form/SelectFormField';

/** the option standing for "no parent at all"; no workplace can carry id 0 */
const ROOT_TARGET = 0;

interface Props {
  tree: WorkplaceTree;
  node: WorkplaceNode;
  isSaving: boolean;
  onCancel: () => void;
  /** null moves the workplace out to the roots */
  onConfirm: (parentId: number | null) => void;
}

/**
 * The keyboard nests a workplace under its previous sibling and drag & drop needs both
 * ends on screen at once; this is the way to a parent that is neither -- far down another
 * branch, or off the top of a long structure.
 */
export const WorkplaceMoveDialog: React.FC<Props> = ({
  tree,
  node,
  isSaving,
  onCancel,
  onConfirm,
}) => {
  const t = useAppTranslation();
  const [targetId, setTargetId] = useState<number | null>(null);

  const options = useMemo(() => {
    const root = {
      id: ROOT_TARGET,
      label: t('workplaces.move.root'),
      path: '',
    };
    // a unit already at the top has nowhere to go by being rooted again
    const targets = targetOptionsFor(tree, node);
    return node.parentId === null ? targets : [root, ...targets];
  }, [tree, node, t]);

  const isValid = targetId !== null && targetId !== node.parentId;

  return (
    <Dialog open={true} onClose={onCancel} maxWidth={'sm'} fullWidth={true}>
      <DialogTitle>{t('workplaces.move.title')}</DialogTitle>
      <DialogContent>
        <p className={'workplace-detail__muted'}>
          {t('workplaces.move.lead', { name: node.unit.name })}
        </p>
        <p className={'workplace-detail__muted'}>
          {t('workplaces.detail.path')} {pathOf(tree, node.id).join(' / ')}
        </p>
        <Autocomplete
          options={options}
          value={options.find((option) => option.id === targetId) ?? null}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, option) => setTargetId(option?.id ?? null)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <span>
                {option.label}
                {!!option.path && (
                  <span className={'workplace-detail__option-path'}> {option.path}</span>
                )}
              </span>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              {...pickerInputProps}
              slotProps={pickerSlotProps(params)}
              variant={'standard'}
              label={t('workplaces.move.target')}
              required={true}
              error={targetId !== null && !isValid}
              helperText={
                targetId !== null && !isValid ? t('workplaces.move.sameParent') : undefined
              }
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color={'inherit'} onClick={onCancel} disabled={isSaving}>
          {t('form.cancel')}
        </Button>
        <Button
          color={'primary'}
          variant={'contained'}
          disabled={!isValid || isSaving}
          onClick={() => onConfirm(targetId === ROOT_TARGET ? null : targetId)}
        >
          {t('workplaces.move.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
